/**
 * useReturnDisposition — shared state + submit logic for the returns-disposition action
 * (Cluster 8, Architecture Rule 10). Consumed by ReturnsInspection + ReturnsDisposition so
 * the two queues drive the same DispositionModal and submit_disposition RPC. Owns the
 * selected-return target, submit state, inline error, success toast, and a refresh signal
 * the QueuePage re-fetches on.
 */
import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { ToastState } from '../components/ui/Toast';
import type { DispositionTarget } from '../components/returns/DispositionModal';

type LooseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

interface DispositionEnvelope {
  ok?: boolean;
  userErrors?: { message?: string }[] | null;
}

export interface UseReturnDispositionResult {
  target: DispositionTarget | null;
  submitting: boolean;
  error: string | null;
  toast: ToastState | null;
  setToast: (t: ToastState | null) => void;
  refreshSignal: number;
  open: (row: DispositionTarget) => void;
  cancel: () => void;
  confirm: (outcome: string, notes: string) => Promise<void>;
}

export function useReturnDisposition(): UseReturnDispositionResult {
  const [target, setTarget] = useState<DispositionTarget | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [refreshSignal, setRefreshSignal] = useState<number>(0);

  const open = useCallback((row: DispositionTarget) => {
    setError(null);
    setTarget(row);
  }, []);

  const cancel = useCallback(() => {
    if (!submitting) {
      setTarget(null);
      setError(null);
    }
  }, [submitting]);

  const confirm = useCallback(
    async (outcome: string, notes: string): Promise<void> => {
      if (!target) return;
      setSubmitting(true);
      setError(null);
      const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;
      const { data, error: rpcError } = await callRpc('submit_disposition', {
        p_return_id: target.id,
        p_outcome: outcome,
        p_notes: notes.trim() || null,
        p_condition: null,
      });
      const envelope = (data ?? null) as DispositionEnvelope | null;
      if (rpcError) {
        setError(rpcError.message);
        setSubmitting(false);
        return;
      }
      if (envelope && envelope.ok === false) {
        setError(envelope.userErrors?.[0]?.message || 'Could not save disposition');
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      setTarget(null);
      setToast({ kind: 'success', message: 'Disposition saved' });
      setRefreshSignal((s) => s + 1);
    },
    [target]
  );

  return { target, submitting, error, toast, setToast, refreshSignal, open, cancel, confirm };
}
